<template>
  <div
    v-if="items.length > 1"
    class="fbt-wrapper"
  >
    <ModalOverlay
      id="pros-fbt-error"
      width="30rem"
      title="Oops"
      @close="closeModal"
    >
      <template #body>
        <div class="grid-x">
          <div class="cell">
            <div class="add-all-bag-cta-overlay grid-x">
              <div class="body cell">
                <p class="error-message">
                  Something went wrong.
                </p>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="grid-x grid-margin-x">
          <button
            class="cell button small-12 medium-6 secondary"
            @click="() => {
              toggleModal('pros-fbt-error');
              closeModal()
            }"
          >
            GOT IT
          </button>
        </div>
      </template>
    </ModalOverlay>
    <component
      :is="headerType"
      v-if="title"
      :class="headerClass"
    >
      {{ title }}
    </component>
    <div class="grid-x fbt-content">
      <div class="small-1 medium-1 large-1 cell" />

      <div class="small-10 cell pros-fbt-image-container">
        <div
          id="pros-fbt-image-grid"
          class="grid-x align-center"
        >
          <div
            id="imageId-6666794"
            class="small-5 medium-5 large-5 cell pros-fbt-thumbnail-image"
          >
            <Picture
              :image-url="mainImage1"
              :skeleton="true"
              :image-intrinsic-width="skeletonWidth"
              alt="product image"
            />
          </div>
          <div class="small-2 medium-2 large-2 cell icon-plus-container fbt-single-plus-icon">
            <span class="margin-left-xs margin-right-xs accordion-plus-black-large pros-plus-sign" />
          </div>
          <div
            id="imageId-10823590"
            class="small-5 medium-5 large-5 cell pros-fbt-thumbnail-image"
          >
            <a
              :href="items[1].identifier.productUrl"
              :target="target"
              :rel="rel"
              @click="linkClickEvent(items[1])"
            >
              <Picture
                :image-url="mainImage2"
                :skeleton="true"
                :image-intrinsic-width="skeletonWidth"
                alt="product image"
              />
            </a>
          </div>
        </div>
      </div>

      <div class="small-1 medium-1 large-1 cell" />
      <Form class="form-wrapper">
        <div class="margin-left-s margin-right-s">
          <Checkbox
            v-model="checkboxValues"
            name="checkbox"
            :options="checkBoxOptions"
          />
        </div>
        <div class="btn-wrapper margin-top-m">
          <div
            class="button emphasis cell"
            :class="{ 'disabled': (isLoading || (!isLoading && !checkboxValues.length)) }"
            @click="onAddToBagClick"
          >
            <Spinner v-if="isLoading" />
            {{ buttonText }}
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script>
import Checkbox from '@atomic-ui/checkbox';
import Form from '@atomic-ui/form';
import { ModalOverlay, SET_MODAL_OVERLAY_VISIBILITY } from '@atomic-ui/modalOverlay';
import Picture from '@atomic-ui/picture';
import Spinner from '@atomic-ui/spinner';
import { computed, h } from 'vue';
import { mapMutations, mapState } from 'vuex';
import { prosFbtEventTag } from '../../analytics/analyticsEvent';
import { useAddToBag } from '../../composables/useAddToBag';
import fetchProducts from '../../services/fetchProducts';
import constants from '../../utils/constants';
import { isValidProduct } from '../../utils/utils';

const BASE_URL = 'https://slimages.macysassets.com/is/image/MCY/products/';
const { ADD_TO_BAG } = constants;

// static values for calculating skeleton width
const plusSignWidth = 56;
const borderWidth = 2;
const margin = 112;
const outedPadding = 40;

export default {
  name: 'ProsFbt',
  components: {
    Picture,
    Form,
    Checkbox,
    Spinner,
    ModalOverlay,
  },
  props: {
    configData: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const {
      productData: items, productIds: checkboxValues, toggleLoading, toggleErrorMessage, onAddToBagClick,
    } = useAddToBag();
    const isLoading = computed(() => toggleLoading.value);
    return {
      items,
      checkboxValues,
      isLoading,
      toggleErrorMessage,
      onAddToBagClick,
    };
  },
  data() {
    return {
      buttonText: '',
      recommendationData: this.configData.recommendationData || {
        recommendedIdsList: [],
      },
      title: this.configData.title || '',
    };
  },
  computed: {
    checkBoxOptions() {
      return this.items.map((item, index) => this.createCheckboxOption(item, index));
    },
    headerType() {
      return this.isMobileScreenSize() ? 'h5' : 'h4';
    },
    headerClass() {
      return `margin-bottom-${this.isDesktopScreenSize ? 'xs' : 's'}`;
    },
    mainImage1() {
      return this.getImage(this.items[0]);
    },
    mainImage2() {
      return this.getImage(this.items[1]);
    },
    skeletonWidth() {
      return this.getSkeletonWidth();
    },
    openInNewTab() {
      return this.configData.openInNewTab;
    },
    target() {
      return this.openInNewTab ? '_blank' : '_self';
    },
    rel() {
      return this.openInNewTab ? 'noopener' : null;
    },
    ...mapState('modalOverlayStore', ['showModal']),
  },
  watch: {
    toggleErrorMessage(newVal) {
      if (newVal) {
        this.toggleModal('pros-fbt-error');
      }
    },
    checkboxValues: {
      handler() {
        this.updateButtonText();
      },
      deep: true,
    },
    items(newVal) {
      if (newVal.length > 0) {
        prosFbtEventTag({
          rtoData: this.recommendationData,
          zone: this.configData.recommendationConfig?.zone,
          items: this.items && this.items[1] ? [this.items[1]] : this.items,
          event: 'content view',
        });
      }
    },
  },
  mounted() {
    this.getProductsAndValidate();
  },
  methods: {
    async getProductsAndValidate() {
      if (
        !this.configData.recommendationConfig?.productId
        || !this.configData.recommendationData?.recommendedIdsList?.length
        || !this.configData.transformer
      ) return;

      const mainItem = fetchProducts(
        [this.configData.recommendationConfig.productId],
        1,
        undefined,
        true,
      );
      const additionalItem = fetchProducts(
        this.configData.recommendationData.recommendedIdsList
          .map((item) => item.id)
          .slice(1),
        1,
        isValidProduct,
        true,
      );
      this.items = (await Promise.all([mainItem, additionalItem]))
        .filter((i) => Array.isArray(i) && i.length > 0)
        .map((v, i) => {
          const {
            meta: {
              analytics: { data },
            },
            product: [product],
          } = v[0];
          return {
            ...product,
            metadata: data,
            choiceId: this.configData.recommendationData.choiceId,
            position: i + 1,
          };
        });
      this.updateButtonText();
    },
    getPrice(tieredPrice = []) {
      if (!tieredPrice.length) { return 0; }
      const initialPrice = tieredPrice[0]?.values[0]?.value;
      const salePriceObj = tieredPrice.find((priceObj, index) => index && priceObj?.values && priceObj?.values[0]?.type === 'discount');
      return salePriceObj?.values[0].value || initialPrice;
    },
    buildPriceElements(tieredPrice = []) {
      let salePrice = tieredPrice.find((priceObj, index) => index > 0 && priceObj?.values && priceObj?.values[0]?.type === 'discount');
      let priceElements = [
        h(
          'div',
          {
            style: `font-weight: ${salePrice ? '500' : 'bold'}; margin-top: 8px;`,
            class: salePrice ? 'price-strike' : '',
          },
          tieredPrice[0]?.values[0]?.formattedValue,
        ),
      ];
      if (tieredPrice.length === 1 && tieredPrice[0]?.values[0]?.type === 'discount') {
        priceElements = [];
        salePrice = tieredPrice[0];
      }
      if (salePrice) {
        let priceText = salePrice.values[0]?.formattedValue;
        if (salePrice.label) {
          priceText = `${salePrice.label.replace('[PRICE]', '').trim()} ${priceText}`;
        }
        priceElements.push(h(
          'div',
          { style: 'font-weight: bold;', class: 'price-discount' },
          priceText,
        ));
      }
      return priceElements;
    },
    getImage(item) {
      return `${BASE_URL}${item.imagery?.images[0].filePath}`;
    },
    updateButtonText() {
      const checkedItemsLength = this.checkboxValues.length;
      if (checkedItemsLength === 2) {
        const [first, second] = this.checkboxValues.map(
          (i) => +i.split('-_-')[1],
        );
        const totalPrice = (first + second).toFixed(2);
        this.buttonText = `Buy Together $${totalPrice}`;
      } else if (checkedItemsLength === 1) {
        const price = (+this.checkboxValues[0]?.split('-_-')[1]).toFixed(2);
        this.buttonText = `${ADD_TO_BAG} $${price}`;
      } else {
        this.buttonText = ADD_TO_BAG;
      }
    },
    isMobileScreenSize() {
      const { innerWidth } = window;
      return innerWidth < 600;
    },
    isTabletScreenSize() {
      const { innerWidth } = window;
      return innerWidth >= 600 && innerWidth < 1024;
    },
    isDesktopScreenSize() {
      const { innerWidth } = window;
      return innerWidth >= 1024;
    },
    getSkeletonWidth() {
      const { innerWidth } = window;
      // default approximated skeleton width for desktop
      if (innerWidth >= 1024) return 130;
      return (
        (innerWidth - plusSignWidth - borderWidth - margin - outedPadding) / 2
      );
    },
    linkClickEvent(i) {
      prosFbtEventTag({
        rtoData: this.recommendationData,
        zone: this.configData.recommendationConfig?.zone,
        items: [i],
        event: 'recommendation link click',
      });
    },
    createCheckboxOption(item, index) {
      const isTablet = this.isTabletScreenSize();
      const value = `${item.identifier.productId}-_-${this.getPrice(item.pricing?.price.tieredPrice)}`;
      this.checkboxValues.push(value);
      return {
        label: h(
          index === 1 ? 'a' : 'span',
          {
            style: !isTablet && 'display: inline-block;',
            ...(index === 1 && {
              class: 'menu-link-body',
              href: item.identifier.productUrl,
              onClick: (e) => {
                e.preventDefault();
                this.linkClickEvent(item);
                // eslint-disable-next-line no-restricted-globals
                location.assign(item.identifier.productUrl);
              },
            }),
          },
          `${item.detail?.brand?.name} ${item.detail?.name}`,
        ),
        value,
        additionalText: h(
          'div',
          { style: 'margin-left: 32px; flex-basis: 100%;' },
          this.buildPriceElements(item.pricing?.price.tieredPrice),
        ),
        labelType: 'html',
      };
    },
    closeModal() {
      this.toggleErrorMessage = false;
    },
    ...mapMutations('modalOverlayStore', { toggleModal: SET_MODAL_OVERLAY_VISIBILITY }),
  },
};
</script>

<style scoped lang="scss">
@import '~@core/csg/src/scss/encodedIcons/mcom/_accordion-plus-black-large';

.fbt-wrapper {
  margin: 0 auto;

  .form-wrapper {
    width: 100%;

    :deep(.checkbox-wrapper) {
      flex-flow: row wrap;
      margin-top: rem-calc(17);

      input[type="checkbox"] {
        align-self: flex-start;
      }

      .price-discount {
        color: $sale-color;
      }

      label {
        inline-size: 85%;
      }
    }
  }
}

.fbt-content {
  border: 1px solid $gray-3-color;

  .pros-fbt-image-container {
    padding-top: rem-calc(24);
  }
}

.images-wrapper {
  display: flex;
  align-items: center;
}

.fbt-single-plus-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-wrapper {
  background-color: $background-color;
  padding: 24px;

  button {
    width: 100%;
  }
}

.pros-plus-sign {
  flex-shrink: 0;
}
</style>
